/**
 * Returns a date shifted by the requested number of years.
 */
export function addYears(date, years) {
  const result = new Date(date)
  result.setFullYear(result.getFullYear() + years)

  return result
}

/**
 * Formats Date for native date inputs.
 */
export function formatDateForInput(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Formats Unix timestamps received from backend admission period settings.
 */
export function formatUnixDateTime(timestamp) {
  const date = new Date(Number(timestamp) * 1000)

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Extracts digits from an input value and optionally limits their count.
 */
export function normalizeDigits(value, maxLength = null) {
  const digits = String(value || '').replace(/\D/g, '')

  if (!maxLength) {
    return digits
  }

  return digits.slice(0, maxLength)
}

/**
 * Applies the SNILS mask: 000-000-000 00.
 */
export function formatSnils(value) {
  const digits = normalizeDigits(value, 11)
  const parts = []

  if (digits.length <= 3) {
    return digits
  }

  parts.push(digits.slice(0, 3))

  if (digits.length <= 6) {
    return `${parts[0]}-${digits.slice(3)}`
  }

  parts.push(digits.slice(3, 6))

  if (digits.length <= 9) {
    return `${parts.join('-')}-${digits.slice(6)}`
  }

  parts.push(digits.slice(6, 9))

  return `${parts.join('-')} ${digits.slice(9)}`
}

/**
 * Applies the Russian phone mask with fixed +7 prefix.
 */
export function formatPhone(value) {
  const rawValue = String(value || '').trim()
  let digits = normalizeDigits(value)

  if ((rawValue.startsWith('+7') && digits.startsWith('7')) ||
    ((digits.startsWith('7') || digits.startsWith('8')) && digits.length === 11)) {
    digits = digits.slice(1)
  }

  digits = digits.slice(0, 10)

  if (!digits.length) {
    return '+7'
  }

  if (digits.length <= 3) {
    return `+7(${digits}`
  }

  if (digits.length <= 6) {
    return `+7(${digits.slice(0, 3)})${digits.slice(3)}`
  }

  if (digits.length <= 8) {
    return `+7(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  return `+7(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`
}

/**
 * Normalizes copied person names before validation and saving.
 */
export function normalizePersonName(value) {
  return String(value || '')
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u02BC]/g, "'")
    .replace(/[ \t\r\n]+/g, ' ')
}

/**
 * Checks allowed characters for person names.
 */
export function isPersonNameValid(value) {
  const normalized = normalizePersonName(value).trim()

  return /^[0-9А-Яа-яЁё' -]+$/.test(normalized)
}

export function isEmailValid(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

/**
 * Checks that the phone mask contains all ten user-entered digits.
 */
export function isPhoneComplete(value) {
  let digits = normalizeDigits(value)

  if (digits.startsWith('7') && digits.length === 11) {
    digits = digits.slice(1)
  }

  return digits.length === 10
}

export function isBlank(value) {
  return value === null ||
    value === undefined ||
    String(value).trim() === ''
}
